import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik } from 'formik';
import dynamicfunction from '../../../utils/dynamicfunction';
import { cn, getContentKeyAccToLang } from '../../../utils/utils';
import {
  getCustodianDetails,
  getCustodianDetailsDropdowns,
  getKycCustodianData,
  saveCorpKycCustodianInfo,
  saveCustodianDetails,
} from '../../../store/actions/corporateAction';
import { custodian_details_validation_schema } from '../../../validations_schemas/custodian_details_schema';
import { ErrorText, RadioButton, RowView, Text } from '../components';
import Button from '../../../components/Button';
import CustomInput from '../../../components/CustomInput';
import { useNavigate } from 'react-router';
import { logoutUser } from '../../../store/actions/authActions';
import KeepValuesOnLanguageChange from '../../../components/KeepValuesOnLanguageChange';

const CustodianDetails = ({ onDone }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { isArabic, TextWeight } = dynamicfunction();

  const { corporate, language } = useSelector((state) => state.content);
  const { custodian_detail_dropdowns, corporate_post_loading, custodian_detail_data } = useSelector((state) => state.corporate);
  const { client_application_id, is_kyc } = useSelector((state) => state.constant);

  // Unsaved form values captured on language switch; in memory only, so a hard reload clears them.
  const draftValues = useRef(null);

  const getContent = (key) => {
    return getContentKeyAccToLang(corporate?.corporate_custodian_detail?.data?.attributes, key, isArabic);
  };

  useEffect(() => {
    getData();
  }, [language]);

  const getData = async () => {
    await dispatch(getCustodianDetailsDropdowns());
    await dispatch(is_kyc ? getKycCustodianData(client_application_id) : getCustodianDetails(client_application_id));
  }

  const onSubmit = (values) => {
    const api_data = {
      isHaveCustodian: values?.isHaveCustodian === '1' ? true : false,
      custodianName: values?.custodianName,
      address: values?.address,
      accountName: values?.accountName,
      accountNumber: values?.accountNumber,
      corpClientApplicationId: client_application_id,

      toWhomShouldWeSendEachOfTheFollowingClient: values?.toWhomShouldWeSendEachOfTheFollowingClient,
      toWhomShouldWeSendEachOfTheFollowingClientCustodian: values?.toWhomShouldWeSendEachOfTheFollowingClientCustodian,
      toWhomShouldWeSendEachOfTheFollowingClientOther: values?.toWhomShouldWeSendEachOfTheFollowingClientOther,
      toWhomShouldWeSendEachOfTheFollowingDividends: values?.toWhomShouldWeSendEachOfTheFollowingDividends,
      toWhomShouldWeSendEachOfTheFollowingDividendsCustodian: values?.toWhomShouldWeSendEachOfTheFollowingDividendsCustodian,
      toWhomShouldWeSendEachOfTheFollowingDividendsOther: values?.toWhomShouldWeSendEachOfTheFollowingDividendsOther,
      toWhomShouldWeSendEachOfTheFollowingSalesProceeds: values?.toWhomShouldWeSendEachOfTheFollowingSalesProceeds,
      toWhomShouldWeSendEachOfTheFollowingSalesProceedsCustodian: values?.toWhomShouldWeSendEachOfTheFollowingSalesProceedsCustodian,
      toWhomShouldWeSendEachOfTheFollowingSalesProceedsOther: values?.toWhomShouldWeSendEachOfTheFollowingSalesProceedsOther,
      doYouHaveAnExistingPortfolio: values?.doYouHaveAnExistingPortfolio === '1' ? true : false,

      clientIdealInvestmentPortfolioDepositsAndMurabaha: Number(values?.clientIdealInvestmentPortfolioDepositsAndMurabaha) || 0,
      clientIdealInvestmentPortfolioDebitInstruments: Number(Number(values.clientIdealInvestmentPortfolioDebitInstruments) || 0),
      clientIdealInvestmentPortfolioStocks: Number(values?.clientIdealInvestmentPortfolioStocks) || 0,
      clientIdealInvestmentPortfolioRealEstate: Number(values?.clientIdealInvestmentPortfolioRealEstate) || 0,
      clientIdealInvestmentPortfolioDerivativesContracts: Number(values?.clientIdealInvestmentPortfolioDerivativesContracts) || 0,
      clientIdealInvestmentPortfolioAlternativeInvestments: Number(values?.clientIdealInvestmentPortfolioAlternativeInvestments) || 0,
      clientIdealInvestmentPortfolioInvestmentFunds: Number(values?.clientIdealInvestmentPortfolioInvestmentFunds) || 0,
      clientCurrentInvestmentPortfolioDepositsAndMurabaha: Number(values?.clientCurrentInvestmentPortfolioDepositsAndMurabaha) || 0,
      clientCurrentInvestmentPortfolioDebitInstruments: Number(values?.clientCurrentInvestmentPortfolioDebitInstruments) || 0,
      clientCurrentInvestmentPortfolioStocks: Number(values?.clientCurrentInvestmentPortfolioStocks) || 0,
      clientCurrentInvestmentPortfolioRealEstate: Number(values?.clientCurrentInvestmentPortfolioRealEstate) || 0,
      clientCurrentInvestmentPortfolioDerivativesContracts: Number(values?.clientCurrentInvestmentPortfolioDerivativesContracts) || 0,
      clientCurrentInvestmentPortfolioAlternativeInvestments: Number(values?.clientCurrentInvestmentPortfolioAlternativeInvestments) || 0,
      clientCurrentInvestmentPortfolioInvestmentFunds: Number(values?.clientCurrentInvestmentPortfolioInvestmentFunds) || 0,
    };

    dispatch(
      is_kyc
        ? saveCorpKycCustodianInfo(api_data, (res) => {
            values?.isSaveExist ? navigate('/funds/available-funds') : onDone();
          })
        : saveCustodianDetails(api_data, (res) => {
            values?.isSaveExist ? dispatch(logoutUser()) : onDone();
          }),
    );
  };

  const portfolioData = [
    {
      name: getContent('deposits_and_murabaha'),
      clientKey: 'clientIdealInvestmentPortfolioDepositsAndMurabaha',
      currentKey: 'clientCurrentInvestmentPortfolioDepositsAndMurabaha',
    },
    {
      name: getContent('debt_instruments'),
      clientKey: 'clientIdealInvestmentPortfolioDebitInstruments',
      currentKey: 'clientCurrentInvestmentPortfolioDebitInstruments',
    },
    {
      name: getContent('stocks'),
      clientKey: 'clientIdealInvestmentPortfolioStocks',
      currentKey: 'clientCurrentInvestmentPortfolioStocks',
    },
    {
      name: getContent('real_estate'),
      clientKey: 'clientIdealInvestmentPortfolioRealEstate',
      currentKey: 'clientCurrentInvestmentPortfolioRealEstate',
    },
    {
      name: getContent('derivatives_contracts'),
      clientKey: 'clientIdealInvestmentPortfolioDerivativesContracts',
      currentKey: 'clientCurrentInvestmentPortfolioDerivativesContracts',
    },
    {
      name: getContent('alternative_investments'),
      clientKey: 'clientIdealInvestmentPortfolioAlternativeInvestments',
      currentKey: 'clientCurrentInvestmentPortfolioAlternativeInvestments',
    },
    {
      name: getContent('investment_funds'),
      clientKey: 'clientIdealInvestmentPortfolioInvestmentFunds',
      currentKey: 'clientCurrentInvestmentPortfolioInvestmentFunds',
    },
  ];

  const optionsYesOrNO = [
    { id: '1', name: getContent('yes') },
    { id: '0', name: getContent('no') },
  ];

  return (
    <>
      <Formik
        enableReinitialize
        initialValues={draftValues.current ? {
          ...draftValues.current,
          // Re-derive the selected option names from their ids so they're sent in the new language.
          toWhomShouldWeSendEachOfTheFollowingClientCustodian:
            custodian_detail_dropdowns?.certificateRadioBtn?.find((item) => item?.id?.toString() === draftValues.current?.toWhomShouldWeSendEachOfTheFollowingClient?.toString())?.name ||
            draftValues.current?.toWhomShouldWeSendEachOfTheFollowingClientCustodian,
          toWhomShouldWeSendEachOfTheFollowingDividendsCustodian:
            custodian_detail_dropdowns?.dividendsRadioBtn?.find((item) => item?.id?.toString() === draftValues.current?.toWhomShouldWeSendEachOfTheFollowingDividends?.toString())?.name ||
            draftValues.current?.toWhomShouldWeSendEachOfTheFollowingDividendsCustodian,
          toWhomShouldWeSendEachOfTheFollowingSalesProceedsCustodian:
            custodian_detail_dropdowns?.salesRadioBtn?.find((item) => item?.id?.toString() === draftValues.current?.toWhomShouldWeSendEachOfTheFollowingSalesProceeds?.toString())?.name ||
            draftValues.current?.toWhomShouldWeSendEachOfTheFollowingSalesProceedsCustodian,
        } : {
          isHaveCustodian: custodian_detail_data?.isHaveCustodian ? '1' : '0',
          custodianName: custodian_detail_data?.custodianName ?? '',
          address: custodian_detail_data?.address ?? '',
          accountName: custodian_detail_data?.accountName ?? '',
          accountNumber: custodian_detail_data?.accountNumber ?? '',
          doYouHaveAnExistingPortfolio: custodian_detail_data?.doYouHaveAnExistingPortfolio ? '1' : '0',

          toWhomShouldWeSendEachOfTheFollowingClient: custodian_detail_data?.toWhomShouldWeSendEachOfTheFollowingClient ?? '',
          toWhomShouldWeSendEachOfTheFollowingClientCustodian: custodian_detail_data?.toWhomShouldWeSendEachOfTheFollowingClientCustodian ?? '',
          toWhomShouldWeSendEachOfTheFollowingClientOther: custodian_detail_data?.toWhomShouldWeSendEachOfTheFollowingClientOther ?? '',

          toWhomShouldWeSendEachOfTheFollowingDividends: custodian_detail_data?.toWhomShouldWeSendEachOfTheFollowingDividends ?? '',
          toWhomShouldWeSendEachOfTheFollowingDividendsCustodian: custodian_detail_data?.toWhomShouldWeSendEachOfTheFollowingDividendsCustodian ?? '',
          toWhomShouldWeSendEachOfTheFollowingDividendsOther: custodian_detail_data?.toWhomShouldWeSendEachOfTheFollowingDividendsOther ?? '',

          toWhomShouldWeSendEachOfTheFollowingSalesProceeds: custodian_detail_data?.toWhomShouldWeSendEachOfTheFollowingSalesProceeds ?? '',
          toWhomShouldWeSendEachOfTheFollowingSalesProceedsCustodian:
            custodian_detail_data?.toWhomShouldWeSendEachOfTheFollowingSalesProceedsCustodian ?? '',
          toWhomShouldWeSendEachOfTheFollowingSalesProceedsOther: custodian_detail_data?.toWhomShouldWeSendEachOfTheFollowingSalesProceedsOther ?? '',

          clientIdealInvestmentPortfolioDepositsAndMurabaha: custodian_detail_data?.clientIdealInvestmentPortfolioDepositsAndMurabaha ?? '',
          clientIdealInvestmentPortfolioDebitInstruments: custodian_detail_data?.clientIdealInvestmentPortfolioDebitInstruments ?? '',
          clientIdealInvestmentPortfolioStocks: custodian_detail_data?.clientIdealInvestmentPortfolioStocks ?? '',
          clientIdealInvestmentPortfolioRealEstate: custodian_detail_data?.clientIdealInvestmentPortfolioRealEstate ?? '',
          clientIdealInvestmentPortfolioDerivativesContracts: custodian_detail_data?.clientIdealInvestmentPortfolioDerivativesContracts ?? '',
          clientIdealInvestmentPortfolioAlternativeInvestments: custodian_detail_data?.clientIdealInvestmentPortfolioAlternativeInvestments ?? '',
          clientIdealInvestmentPortfolioInvestmentFunds: custodian_detail_data?.clientIdealInvestmentPortfolioInvestmentFunds ?? '',
          clientCurrentInvestmentPortfolioDepositsAndMurabaha: custodian_detail_data?.clientCurrentInvestmentPortfolioDepositsAndMurabaha ?? '',
          clientCurrentInvestmentPortfolioDebitInstruments: custodian_detail_data?.clientCurrentInvestmentPortfolioDebitInstruments ?? '',
          clientCurrentInvestmentPortfolioStocks: custodian_detail_data?.clientCurrentInvestmentPortfolioStocks ?? '',
          clientCurrentInvestmentPortfolioRealEstate: custodian_detail_data?.clientCurrentInvestmentPortfolioRealEstate ?? '',
          clientCurrentInvestmentPortfolioDerivativesContracts: custodian_detail_data?.clientCurrentInvestmentPortfolioDerivativesContracts ?? '',
          clientCurrentInvestmentPortfolioAlternativeInvestments: custodian_detail_data?.clientCurrentInvestmentPortfolioAlternativeInvestments ?? '',
          clientCurrentInvestmentPortfolioInvestmentFunds: custodian_detail_data?.clientCurrentInvestmentPortfolioInvestmentFunds ?? '',
          isSaveExist: false,
        }}
        onSubmit={(values) => {
          onSubmit(values);
        }}
        validationSchema={custodian_details_validation_schema}
        validateOnChange={true}
        validateOnBlur={true}
      >
        {({ handleSubmit, setFieldValue, values, errors, touched, setFieldTouched }) => {
          const totalIdeal =
            Number(values?.clientIdealInvestmentPortfolioDepositsAndMurabaha) +
            Number(values?.clientIdealInvestmentPortfolioDebitInstruments) +
            Number(values?.clientIdealInvestmentPortfolioStocks) +
            Number(values?.clientIdealInvestmentPortfolioRealEstate) +
            Number(values?.clientIdealInvestmentPortfolioDerivativesContracts) +
            Number(values?.clientIdealInvestmentPortfolioAlternativeInvestments) +
            Number(values?.clientIdealInvestmentPortfolioInvestmentFunds);

          const totalCurrent =
            Number(values?.clientCurrentInvestmentPortfolioDepositsAndMurabaha) +
            Number(values?.clientCurrentInvestmentPortfolioDebitInstruments) +
            Number(values?.clientCurrentInvestmentPortfolioStocks) +
            Number(values?.clientCurrentInvestmentPortfolioRealEstate) +
            Number(values?.clientCurrentInvestmentPortfolioDerivativesContracts) +
            Number(values?.clientCurrentInvestmentPortfolioAlternativeInvestments) +
            Number(values?.clientCurrentInvestmentPortfolioInvestmentFunds);

          return (
            <div className=''>
              <KeepValuesOnLanguageChange onLanguageChange={(currentValues) => (draftValues.current = currentValues)} />
              <div>
                <RadioButton
                  label={getContent('have_custodian')}
                  value={values?.isHaveCustodian}
                  onChange={(e) => {
                    setFieldValue('isHaveCustodian', e);
                    setFieldTouched('isHaveCustodian', false);
                  }}
                  options={optionsYesOrNO}
                  required={false}
                />
                <ErrorText text={getContent(touched?.isHaveCustodian ? errors?.isHaveCustodian : '')} className='block'/>
              </div>

              {values?.isHaveCustodian === '1' && (
                <>
                  <RowView className='gap-7 pt-3 sm:pt-5' isResponsive>
                    <CustomInput
                      isRounded
                      isRequired={true}
                      label={getContent('custodian_name')}
                      value={values?.custodianName}
                      onChange={(e) => {
                        setFieldValue('custodianName', e.target.value);
                        setFieldTouched('custodianName', true, false);
                      }}
                      placeholder={getContent('placeholder_name')}
                      errorMessage={getContent(touched?.custodianName ? errors?.custodianName : '')}
                    />
                    <CustomInput
                      isRounded
                      isRequired={true}
                      label={getContent('address_for_correspondence')}
                      value={values?.address}
                      onChange={(e) => {
                        setFieldValue('address', e.target.value);
                        setFieldTouched('address', true, false);
                      }}
                      placeholder={getContent('placeholder_address_for_correspondence')}
                      errorMessage={getContent(touched?.address ? errors?.address : '')}
                    />
                    <CustomInput
                      isRounded
                      isRequired={true}
                      label={getContent('account_name')}
                      value={values?.accountName}
                      onChange={(e) => {
                        setFieldValue('accountName', e.target.value);
                        setFieldTouched('accountName', true, false);
                      }}
                      placeholder={getContent('placeholder_name')}
                      errorMessage={getContent(touched?.accountName ? errors?.accountName : '')}
                    />
                  </RowView>

                  <RowView className='gap-7 pt-3 sm:pt-5' isResponsive>
                    <CustomInput
                      isRounded
                      isRequired={true}
                      label={getContent('account_number')}
                      value={values?.accountNumber}
                      onChange={(e) => {
                        setFieldValue('accountNumber', e.target.value);
                        setFieldTouched('accountNumber', true, false);
                      }}
                      placeholder={getContent('placeholder_number')}
                      errorMessage={getContent(touched?.accountNumber ? errors?.accountNumber : '')}
                    />
                  </RowView>
                </>
              )}

              <div className='mt-5'>
                <Text
                  text={getContent('forwarding_instructions_heading')}
                  isTruncate={false}
                  weight={TextWeight.medium}
                  isRequired
                  className={'block'}
                />
              </div>

              <RowView className='gap-7 mt-3 sm:mt-4' isResponsive>
                <div className='w-full'>
                  <RadioButton
                    label={getContent('certificate')}
                    value={values?.toWhomShouldWeSendEachOfTheFollowingClient}
                    onChange={(e) => {
                      setFieldValue('toWhomShouldWeSendEachOfTheFollowingClient', e);
                      setFieldValue(
                        'toWhomShouldWeSendEachOfTheFollowingClientCustodian',
                        custodian_detail_dropdowns?.certificateRadioBtn?.find((item) => item?.id?.toString() === e?.toString())?.name || '',
                      );
                      setFieldTouched('toWhomShouldWeSendEachOfTheFollowingClient', false, false);
                    }}
                    options={custodian_detail_dropdowns?.certificateRadioBtn}
                    required={false}
                  />
                  <ErrorText
                    text={getContent(touched?.toWhomShouldWeSendEachOfTheFollowingClient ? errors?.toWhomShouldWeSendEachOfTheFollowingClient : '')} className='block'
                  />

                  {Number(values?.toWhomShouldWeSendEachOfTheFollowingClient) === 803 && (
                    <CustomInput
                      isRounded
                      isRequired={true}
                      label={getContent('please_specify')}
                      value={values?.toWhomShouldWeSendEachOfTheFollowingClientOther}
                      onChange={(e) => {
                        setFieldValue('toWhomShouldWeSendEachOfTheFollowingClientOther', e.target.value);
                        setFieldTouched('toWhomShouldWeSendEachOfTheFollowingClientOther', true, false);
                      }}
                      labelDivClass={"h-9 sm:h-6 md:h-9 xl:h-6"}
                      placeholder={getContent('placeholder_number')}
                      errorMessage={getContent(
                        touched?.toWhomShouldWeSendEachOfTheFollowingClientOther ? errors?.toWhomShouldWeSendEachOfTheFollowingClientOther : '',
                      )}
                    />
                  )}
                </div>
                <div className='w-full'>
                  <RadioButton
                    label={getContent('dividends_other_income')}
                    value={values?.toWhomShouldWeSendEachOfTheFollowingDividends}
                    onChange={(e) => {
                      setFieldValue('toWhomShouldWeSendEachOfTheFollowingDividends', e);
                      setFieldValue(
                        'toWhomShouldWeSendEachOfTheFollowingDividendsCustodian',
                        custodian_detail_dropdowns?.dividendsRadioBtn?.find((item) => item?.id?.toString() === e?.toString())?.name || '',
                      );
                      setFieldTouched('toWhomShouldWeSendEachOfTheFollowingDividends', false);
                    }}
                    options={custodian_detail_dropdowns?.dividendsRadioBtn}
                    required={false}
                  />
                  <ErrorText
                    text={getContent(
                      touched?.toWhomShouldWeSendEachOfTheFollowingDividends ? errors?.toWhomShouldWeSendEachOfTheFollowingDividends : '',
                    )} className='block'
                  />

                  {Number(values?.toWhomShouldWeSendEachOfTheFollowingDividends) === 803 && (
                    <CustomInput
                      isRounded
                      isRequired={true}
                      label={getContent('please_specify')}
                      value={values?.toWhomShouldWeSendEachOfTheFollowingDividendsOther}
                      onChange={(e) => {
                        setFieldValue('toWhomShouldWeSendEachOfTheFollowingDividendsOther', e.target.value);
                        setFieldTouched('toWhomShouldWeSendEachOfTheFollowingDividendsOther', true, false);
                      }}
                      placeholder={getContent('placeholder_number')}
                      labelDivClass={"h-9 sm:h-6 md:h-9 xl:h-6"}
                      errorMessage={getContent(
                        touched?.toWhomShouldWeSendEachOfTheFollowingDividendsOther ? errors?.toWhomShouldWeSendEachOfTheFollowingDividendsOther : '',
                      )}
                    />
                  )}
                </div>

                <div className='w-full'>
                  <RadioButton
                    label={getContent('sales_proceeds')}
                    value={values?.toWhomShouldWeSendEachOfTheFollowingSalesProceeds}
                    onChange={(e) => {
                      setFieldValue('toWhomShouldWeSendEachOfTheFollowingSalesProceeds', e);
                      setFieldValue(
                        'toWhomShouldWeSendEachOfTheFollowingSalesProceedsCustodian',
                        custodian_detail_dropdowns?.salesRadioBtn?.find((item) => item?.id?.toString() === e?.toString())?.name || '',
                      );
                      setFieldTouched('toWhomShouldWeSendEachOfTheFollowingSalesProceeds', false);
                    }}
                    options={custodian_detail_dropdowns?.salesRadioBtn}
                    required={false}
                  />
                  <ErrorText
                    text={getContent(
                      touched?.toWhomShouldWeSendEachOfTheFollowingSalesProceeds ? errors?.toWhomShouldWeSendEachOfTheFollowingSalesProceeds : '',
                    )} className='block'
                  />

                  {Number(values?.toWhomShouldWeSendEachOfTheFollowingSalesProceeds) === 803 && (
                    <CustomInput
                      isRounded
                      isRequired={true}
                      label={getContent('please_specify')}
                      value={values?.toWhomShouldWeSendEachOfTheFollowingSalesProceedsOther}
                      onChange={(e) => {
                        setFieldValue('toWhomShouldWeSendEachOfTheFollowingSalesProceedsOther', e.target.value);
                        setFieldTouched('toWhomShouldWeSendEachOfTheFollowingSalesProceedsOther', true, false);
                      }}
                      labelDivClass={"h-9 sm:h-6 md:h-9 xl:h-6"}
                      placeholder={getContent('placeholder_number')}
                      errorMessage={getContent(
                        touched?.toWhomShouldWeSendEachOfTheFollowingSalesProceedsOther
                          ? errors?.toWhomShouldWeSendEachOfTheFollowingSalesProceedsOther
                          : '',
                      )}
                    />
                  )}
                </div>
              </RowView>

              <div className='mt-4'>
                <RadioButton
                  label={getContent('have_existing_portfolio')}
                  value={values?.doYouHaveAnExistingPortfolio}
                  onChange={(e) => {
                    setFieldValue('doYouHaveAnExistingPortfolio', e);
                    setFieldTouched('doYouHaveAnExistingPortfolio', false);
                  }}
                  options={optionsYesOrNO}
                  required={false}
                />
                <ErrorText text={getContent(touched?.doYouHaveAnExistingPortfolio ? errors?.doYouHaveAnExistingPortfolio : '')} className='block'/>
              </div>

              {values?.doYouHaveAnExistingPortfolio === '1' && (
                <div className='border border-border rounded-lg mt-4 w-full overflow-x-auto'>
                  <RowView className='bg-primary/10 px-4 py-3'>
                    <div className='flex-1' />
                    <RowView className='flex-1'>
                      <Text
                        text={getContent('ideal_investment_portfolio_profile')}
                        color='text-primary'
                        weight={TextWeight.medium}
                        className='w-2/3 block'
                        isTruncate={false}
                      />
                    </RowView>
                    <RowView className='flex-1'>
                      <Text
                        text={getContent('current_investment_portfolio_profile')}
                        color='text-primary'
                        weight={TextWeight.medium}
                        className='w-2/3 block'
                        isTruncate={false}
                      />
                    </RowView>
                  </RowView>
                  {portfolioData?.map((item, index) => (
                    <RowView className='px-4 py-3 border-b border-b-border' key={index}>
                      <Text text={item?.name} className='flex-1' />

                      <input
                        value={values?.[item?.clientKey]}
                        className={cn('outline-none flex-1', isArabic ? 'text-end' : 'text-start')}
                        placeholder='0'
                        onChange={(e) => {
                          const val = e.target.value;
                          if (/^\d*$/.test(val)) {
                            setFieldValue(item?.clientKey, val);
                          }
                        }}
                        maxLength={3}
                      />

                      <input
                        value={values?.[item?.currentKey]}
                        className={cn('outline-none flex-1', isArabic ? 'text-end' : 'text-start')}
                        placeholder='0'
                        onChange={(e) => {
                          const val = e.target.value;
                          if (/^\d*$/.test(val)) {
                            setFieldValue(item?.currentKey, val);
                          }
                        }}
                        maxLength={3}
                      />
                    </RowView>
                  ))}

                  <RowView className='px-4 py-3 border-b border-b-border'>
                    <Text text={getContent('totalItems')} className='flex-1' />
                    <Text text={`${totalIdeal ?? 0} %`} className='flex-1' />
                    <Text text={`${totalCurrent ?? 0} %`} className='flex-1' />
                  </RowView>
                </div>
              )}
              <ErrorText
                className={'mt-1.5 block'}
                text={(totalIdeal !== 100 || totalCurrent !== 100) && values?.doYouHaveAnExistingPortfolio === '1' ? getContent('invalid_total') : ''}
              />

              <div className='w-full h-0.5 bg-border mt-3 sm:mt-5'></div>
              <RowView className='gap-7 pt-3 sm:pt-5'>
                <Button
                  text={getContent('save_and_proceed')}
                  color={'text-white hover:text-primary '}
                  bgColor='bg-primary hover:bg-transparent hover:border-primary'
                  className={'border border-transparent w-44 px-0 text-center'}
                  loading={corporate_post_loading}
                  isDisabled={
                    corporate_post_loading || ((totalIdeal !== 100 || totalCurrent !== 100) && values?.doYouHaveAnExistingPortfolio === '1')
                  }
                  onClick={() => {
                    setFieldValue('isSaveExist', false);
                    handleSubmit();
                  }}
                />
                <Button
                  text={getContent('save_and_exit')}
                  color={'text-primary hover:text-white'}
                  bgColor='bg-white hover:bg-primary'
                  className={'border border-primary w-44 px-0 text-center'}
                  isDisabled={
                    corporate_post_loading || ((totalIdeal !== 100 || totalCurrent !== 100) && values?.doYouHaveAnExistingPortfolio === '1')
                  }
                  onClick={() => {
                    setFieldValue('isSaveExist', true);
                    handleSubmit();
                  }}
                />
              </RowView>
            </div>
          );
        }}
      </Formik>
    </>
  );
};

export default CustodianDetails;
